import { motion, MotionValue, useMotionValue } from "framer-motion";
import React, { useEffect, useRef } from "react";
import "./horizontal-slider-scrollbar.scss";

type ScrollBarProps = {
  containerWidth: number;
  sliderWidth: number;
  thumbWidth: number;
  x: MotionValue<number>;
  offset?: MotionValue<number>;
  scrollBarRef: React.RefObject<HTMLDivElement>;
  className?: string;
};

export const HorizontalSliderScrollbar: React.FC<ScrollBarProps> = ({
  containerWidth,
  thumbWidth,
  sliderWidth,
  x,
  offset,
  scrollBarRef,
  className,
}) => {
  const thumbRef = useRef<HTMLDivElement>(null);
  const MotionThumb: any = motion.div;
  const internalOffset = useMotionValue(0);

  if (sliderWidth <= containerWidth) return null;

  // Keep thumb offset in sync with content x and dimensions.
  useEffect(() => {
    const sync = () => {
      const maxContentTravel = containerWidth - sliderWidth; // negative or zero
      const trackWidth = scrollBarRef.current?.clientWidth ?? containerWidth;
      const maxThumbTravel = Math.max(0, trackWidth - thumbWidth); // positive
      const currentX = x.get();
      const ratio =
        maxContentTravel === 0
          ? 0
          : Math.min(1, Math.max(0, (0 - currentX) / (0 - maxContentTravel)));
      const nextOffset = ratio * maxThumbTravel;
      (offset ?? internalOffset).set(nextOffset);
    };
    // Older Framer Motion versions use onChange without return unsubscribe
    // @ts-ignore
    x.onChange(sync);
    sync();
    return () => {
      // no-op cleanup for older versions
    };
  }, [x, offset, internalOffset, containerWidth, sliderWidth, thumbWidth, scrollBarRef]);

  const handleThumbDrag = () => {
    // One-to-one mapping using MotionValue; avoid layout reads for drag loop
    const mv = (offset ?? internalOffset);
    const currentLeft = mv.get();
    const trackWidth = scrollBarRef.current?.clientWidth ?? containerWidth;
    const maxThumbTravel = Math.max(0, trackWidth - thumbWidth);
    if (maxThumbTravel <= 0) return;
    const ratio = currentLeft / maxThumbTravel;
    const newX = ratio * (containerWidth - sliderWidth);
    x.set(newX);
  };

  const handleScrollBarClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === thumbRef.current) return;
    const scrollBar = scrollBarRef.current;
    if (!scrollBar) return;
    const bounds = scrollBar.getBoundingClientRect();
    const clickX = event.clientX - bounds.left;
    const scrollPercent = clickX / bounds.width;
    const clampedPercent = Math.max(0, Math.min(1, scrollPercent));
    const newX = (containerWidth - sliderWidth) * clampedPercent;
    x.set(newX);
  };

  return (
    <div className={["horizontal-slider-scrollbar", className].filter(Boolean).join(" ")}>
      <div
        className="horizontal-slider-scrollbar__track"
        onClick={handleScrollBarClick}
        ref={scrollBarRef}
        role="presentation"
        aria-hidden="true"
      />
      <MotionThumb
        ref={thumbRef}
        className="horizontal-slider-scrollbar__thumb"
        style={{ width: thumbWidth, x: offset ?? internalOffset }}
        drag="x"
        dragConstraints={scrollBarRef}
        dragMomentum={false}
        dragElastic={false}
        onDrag={handleThumbDrag}
      />
    </div>
  );
};

export default HorizontalSliderScrollbar;


